import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { checkFollowStatus, followUser, getFollowCounts, getFollowers, getFollowing, unfollowUser } from '../api/followersApi';


// Hook to get list of users the current user is following
export const useFollowing = (userId = null) => {
  const { setError } = useAuthStore();
  const queryKey = userId ? ['following', userId] : ['following'];

  return useQuery(queryKey, () => getFollowing(userId), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch following list');
    },
    retry: false,
    staleTime: 300000, // 5 minutes
  });
};

// Hook to get list of users who follow the current user
export const useFollowers = (userId = null) => {
  const { setError } = useAuthStore();
  const queryKey = userId ? ['followers', userId] : ['followers'];

  return useQuery(queryKey, () => getFollowers(userId), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch followers list');
    },
    retry: false,
    staleTime: 300000, // 5 minutes
  });
};

// Hook to check if the current user is following another user
export const useFollowStatus = (followingId) => {
  const { setError } = useAuthStore();

  return useQuery(
    ['followStatus', followingId],
    () => checkFollowStatus(followingId),
    {
      onError: (error) => {
        setError(error?.response?.data?.message || 'Failed to check follow status');
      },
      retry: false,
      enabled: !!followingId, // Only run query if followingId is provided
      staleTime: 60000, // 1 minute
    }
  );
};

// Hook to get follower and following counts
export const useFollowCounts = (userId = null) => {
  const { setError } = useAuthStore();
  const queryKey = userId ? ['followCounts', userId] : ['followCounts'];

  return useQuery(queryKey, () => getFollowCounts(userId), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch follow counts');
    },
    retry: false,
    staleTime: 300000, // 5 minutes
  });
};

// Hook to follow a user
export const useFollowUser = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(followUser, {
    onSuccess: (_, followingId) => {
      toast.success('User followed successfully!');
      
      // Invalidate relevant queries
      queryClient.invalidateQueries('following');
      queryClient.invalidateQueries('followCounts');
      queryClient.invalidateQueries(['followStatus', followingId]);
      
      // Optimistically update follow status
      queryClient.setQueryData(['followStatus', followingId], { isFollowing: true });
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to follow user';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

// Hook to unfollow a user
export const useUnfollowUser = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(unfollowUser, {
    onSuccess: (_, followingId) => {
      toast.success('User unfollowed successfully!');
      
      // Invalidate relevant queries
      queryClient.invalidateQueries('following');
      queryClient.invalidateQueries('followCounts');
      queryClient.invalidateQueries(['followStatus', followingId]);
      
      // Optimistically update follow status
      queryClient.setQueryData(['followStatus', followingId], { isFollowing: false });
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to unfollow user';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

// Combined hook for follow/unfollow functionality
export const useFollowActions = () => {
  const followMutation = useFollowUser();
  const unfollowMutation = useUnfollowUser();

  return {
    followUser: followMutation.mutate,
    unfollowUser: unfollowMutation.mutate,
    isFollowLoading: followMutation.isLoading,
    isUnfollowLoading: unfollowMutation.isLoading,
    isFollowError: followMutation.isError,
    isUnfollowError: unfollowMutation.isError,
    followError: followMutation.error,
    unfollowError: unfollowMutation.error
  };
};